import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Session, User } from '@supabase/supabase-js';
import { useProfile } from '@/hooks/useProfile';
import { Profile } from '@/types';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [sessionResolved, setSessionResolved] = useState(false);

  const { data: profile, isLoading: profileLoading } = useProfile(user?.id);

  useEffect(() => {
    let active = true;
    let authEventReceived = false;
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!active || authEventReceived) return;
        setSession(error ? null : session);
        setUser(error ? null : session?.user ?? null);
      } catch {
        if (!active || authEventReceived) return;
        setSession(null);
        setUser(null);
      } finally {
        if (active) setSessionResolved(true);
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!active) return;
        authEventReceived = true;
        setSession(session);
        setUser(session?.user ?? null);
        setSessionResolved(true);
      }
    );

    void getSession();

    return () => {
      active = false;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  // Enquanto a sessão ainda não foi resolvida, ou há um usuário logado cujo
  // perfil ainda está carregando, o app deve mostrar estado de loading.
  const loading = !sessionResolved || (!!user && profileLoading);

  const value = {
    session,
    user,
    profile: profile ?? null,
    loading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};